import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { PaginatorModule } from 'primeng/paginator';
import { MenuModule } from 'primeng/menu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { UserService, User, PagedUsersRequest, PagedUsersResponse, PendingApproval } from '../../../core/services/user.service';
import { AuthService } from '../../../core/services/auth';
import { environment } from '../../../../environments/environment';
import { IconFieldModule } from 'primeng/iconfield';
import { Menu } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { InputIcon } from 'primeng/inputicon';
import { Router } from '@angular/router';
import { PagedSubscriberRequest, PagedSubscriberResponse, Subscriber, SubscriberService } from '../../../core/services/subscriber.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';


@Component({
  selector: 'app-view-subscribers',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    SelectButtonModule,
    PaginatorModule,
    MenuModule,
    ConfirmDialogModule,
    ToastModule,
    CardModule,
    BadgeModule,
    AvatarModule,
    TooltipModule,
    DrawerModule,
    TagModule,
    IconFieldModule,
    InputIcon
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './view-subscribers.html',
  styleUrl: './view-subscribers.scss'
})
export class ViewSubscribers implements OnInit {
  @ViewChild('menu') menu!: Menu;

  subscribers: Subscriber[] = [];
  totalSubscribers = 0;
  loading = false;
  currentPage = 1;
  pageSize = 10;
  pageSizes = [10, 20, 50, 100];

  // User counts cache
  userCountsCache: { [subscriberId: string]: number } = {};



  // Math property for template
  Math = Math;

  // Filters
  searchText = '';
  selectedStatus: any = null;
  selectedUserStatus: any = null;
  selectedArchived: any = false;

  // Sorting
  sortField = 'createdAt';
  sortOrder: 1 | -1 = -1; // -1 for descending (newest first), 1 for ascending (oldest first)

  userStatusOptions = [
    { label: 'All', value: null },
    { label: 'Active', value: true },
    { label: 'Inactive', value: false }
  ];

  archivedOptions = [
    { label: 'Active', value: false },
    { label: 'Archived', value: true }
  ];

  // User actions menu
  actionMenuItems: MenuItem[] = [];
  selectedUser: User | null = null;

  // Drawer for user review
  isDrawerVisible = false;
  selectedSubscriberForReview: Subscriber | null = null;
  subscriberReviewLoading = false;

  constructor(
    private userService: UserService,
    private subscriberService: SubscriberService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router
  ) { }

  ngOnInit() {
    this.loadSubscribers();
    this.setupActionMenu();
  }

  setupActionMenu() {
    this.actionMenuItems = [
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.editUser()
      },
    ];
  }

  updateActionMenuItems(user: User) {
    this.actionMenuItems = [
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.editUser()
      },
    ];
  }

  loadSubscribers() {
    this.loading = true;
    const request: PagedSubscriberRequest = {
      pageIndex: this.currentPage,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      filters: {
        searchText: this.searchText,
        status: this.selectedStatus,
        userStatus: this.selectedUserStatus
      }
    };

    this.subscriberService.getPagedSubscriber(request).subscribe({
      next: (response: PagedSubscriberResponse) => {
        console.log('Subscribers response:', response);
        this.subscribers = response.data || [];
        this.totalSubscribers = response.metadata?.[0]?.total || 0;
        this.loading = false;


        // Preload user counts for all subscribers
        this.preloadUserCounts();


      },
      error: (error: any) => {
        console.error('Error loading subscribers:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load subscribers'
        });
        this.loading = false;
      }
    });
  }

  preloadUserCounts() {
    // Preload user counts for all subscribers to avoid multiple API calls - only count active users
    this.subscribers.forEach(subscriber => {
      if (!this.userCountsCache[subscriber._id]) {
        this.userService.getUsersBySubscriberId(subscriber._id).subscribe({
          next: (users) => {
            // Filter for active users only (status: 'APPROVED' and isActive: true)
            const activeUsers = users.filter(user => 
              user.status === 'APPROVED' && 
              user.isActive === true
            );
            this.userCountsCache[subscriber._id] = activeUsers.length;
          },
          error: (error) => {
            console.error('Error fetching users for subscriber:', error);
            this.userCountsCache[subscriber._id] = 0;
          }
        });
      }
    });
  }



  applyFilters() {
    this.currentPage = 1;
    this.loadSubscribers();
  }

  resetFilters() {
    this.searchText = '';
    this.selectedStatus = null;
    this.selectedUserStatus = null;
    this.selectedArchived = null;
    this.currentPage = 1;
    this.resetSorting();
    // Clear user counts cache when filters are reset
    this.clearUserCountsCache();
    this.loadSubscribers();
  }

  clearUserCountsCache() {
    this.userCountsCache = {};
  }

  resetSorting() {
    this.sortField = 'createdAt';
    this.sortOrder = -1;
  }

  toggleSort() {
    this.sortOrder = this.sortOrder === -1 ? 1 : -1;
    this.loadSubscribers();
  }

  filterPendingUsers() {
    this.selectedStatus = 'PENDING';
    this.currentPage = 1;
    this.loadSubscribers();
  }

  onPageChange(event: any) {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    this.loadSubscribers();
  }

  // New method to open drawer for subscriber review
  openSubscriberReview(subscriberId: string) {
    this.isDrawerVisible = true;
    this.subscriberReviewLoading = true;
    this.selectedSubscriberForReview = null;

    // Find the subscriber from the existing table data first
    const existingSubscriber = this.subscribers.find(sub => sub._id === subscriberId);

    if (existingSubscriber) {
      // Use the existing data which already has the user information
      this.selectedSubscriberForReview = existingSubscriber;
      this.subscriberReviewLoading = false;
      console.log('Selected subscriber for review (from table):', this.selectedSubscriberForReview);
    } else {
      // Fallback to API call if not found in table
      this.subscriberService.findSubscriber(subscriberId).then((response) => {
        const subscriber = response.subscriber || response;
        this.selectedSubscriberForReview = subscriber;
        this.subscriberReviewLoading = false;
        console.log('Selected subscriber for review (from API):', this.selectedSubscriberForReview);
      }).catch((error) => {
        console.error('Error loading subscriber details:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load subscriber details'
        });
        this.subscriberReviewLoading = false;
        this.isDrawerVisible = false;
      });
    }
  }


  // Close drawer
  closeUserReview() {
    this.isDrawerVisible = false;
    this.selectedSubscriberForReview = null;
    this.subscriberReviewLoading = false;
  }

  getStatusBadgeSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' | 'info' {
    switch (status) {
      case 'INVITED':
        return 'info';
      case 'APPROVED':
        return 'success';
      case 'Active':
        return 'success';
      case 'PENDING':
        return 'warn';
      case 'DENIED':
        return 'danger';
      case 'LOCKED':
        return 'danger';
      case 'DEACTIVATED':
        return 'secondary';
      default:
        return 'info';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'INVITED':
        return 'Invited';
      case 'APPROVED':
        return 'Active';
      case 'PENDING':
        return 'Pending';
      case 'DENIED':
        return 'Denied';
      case 'LOCKED':
        return 'Locked';
      case 'DEACTIVATED':
        return 'Deactivated';
      default:
        return status;
    }
  }

  showActionMenu(event: Event, user: User) {
    this.selectedUser = user;
    this.updateActionMenuItems(user);
    event.stopPropagation();
    if (this.menu) {
      this.menu.show(event);
    }
  }

  editUser() {
    if (this.selectedUser) {
      // Navigate to edit user page with user ID
      this.router.navigate(['/admin/add-edit-user'], {
        queryParams: {
          edit: 'true',
          userId: this.selectedUser._id
        }
      });
    }
  }
  getInitials(firstName: string, lastName: string): string {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  }

  getActiveUsers(subscriberId: string): number {
    // Return cached count if available, otherwise return 0
    return this.userCountsCache[subscriberId] || 0;
  }

  refreshUserCount(subscriberId: string) {
    // Refresh user count for a specific subscriber - only count active users
    this.userService.getUsersBySubscriberId(subscriberId).subscribe({
      next: (users) => {
        // Filter for active users only (status: 'APPROVED' and isActive: true)
        const activeUsers = users.filter(user => 
          user.status === 'APPROVED' && 
          user.isActive === true
        );
        this.userCountsCache[subscriberId] = activeUsers.length;
      },
      error: (error) => {
        console.error('Error refreshing user count for subscriber:', error);
        this.userCountsCache[subscriberId] = 0;
      }
    });
  }



  openInNewTab(url: string) {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }


  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getLanguageName(languageCode: string): string {
    if (!languageCode) return 'N/A';
    
    const languageMap: { [key: string]: string } = {
      'si': 'Sinhala',
      'en': 'English',
      'en-US': 'English (US)',
      'en-GB': 'English (UK)',
      'ta': 'Tamil'
    };
    
    return languageMap[languageCode] || languageCode;
  }

  exportUsers() {
    // Implement export functionality
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Export functionality to be implemented'
    });
  }

  addNewUser() {
    this.router.navigate(['/admin/add-edit-user']);
  }




}
